import './PersonalInfoForm.css';

import React, { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

// Matches what BookingForm sends to the backend (`email.trim().toLowerCase()`), so the two
// fields are compared on the value that actually gets stored rather than on what was typed.
const normaliseEmail = (value) => (value || '').trim().toLowerCase();


const PersonalInfoForm = forwardRef(({
    // pass the personal in here as we have got here from a reload of the booking form
    personalInfo,
    setPersonalInfo,
}, ref) => {

    const [validationErrors, setValidationErrors] = useState({});
    const [triggerValidation, setTriggerValidation] = useState(false);
    const [isButtonClicked, setIsButtonClicked] = useState(false);


    // handles the first name changes
    const handleInputChange = (e) => {

        const { name, value, type, checked } = e.target;

        setPersonalInfo({
            ...personalInfo,
            [name]: type === 'checkbox' ? checked : value,
        });

        if (isButtonClicked) {
            // continuously validate form on input change
            setTriggerValidation(true);
        }

    };


    const validateForm = useCallback(() => {

        if (!isButtonClicked) {
            // capture first time button has been clicked
            setIsButtonClicked(true);
        }

        const errors = {};
        if (!personalInfo.first_name) {
            errors.first_name = true;
        }
        if (!personalInfo.last_name) {
            errors.last_name = true;
        }
        if (!personalInfo.email) {
            errors.email = true;
        } else if (!/\S+@\S+\.\S+/.test(personalInfo.email)) {
            errors.email = true;
        }
        // #272: the confirmation field. A mistyped address means the confirmation email --
        // and the door code in it -- goes nowhere, and nothing downstream notices: the booking
        // confirms, the money is taken, and a duplicate door user is created on the controller
        // under an address nobody reads. Four such addresses are in the live database, each
        // with a paid booking behind it.
        //
        // Compared the way the address is actually sent (trimmed, lowercased, in BookingForm),
        // so a stray space or a capital does not read as a mismatch to someone who typed the
        // same thing twice.
        if (!personalInfo.confirm_email) {
            errors.confirm_email = true;
        } else if (normaliseEmail(personalInfo.confirm_email) !== normaliseEmail(personalInfo.email)) {
            errors.confirm_email = true;
        }
        if (!personalInfo.termsAccepted) {
            errors.termsAccepted = 'You must accept the terms and conditions';
        }
        setValidationErrors(errors);

        // determine if form is valid
        const isValid = Object.keys(errors).length === 0

        // Reset trigger after validation so it will be called again if set true
        if (triggerValidation)
            setTriggerValidation(false);

        return isValid;

    }, [isButtonClicked, personalInfo, triggerValidation]);

    useEffect(() => {
        if (triggerValidation) {
            validateForm();
        }
    }, [triggerValidation, validateForm]);

    // Expose validateForm to parent via ref
    useImperativeHandle(ref, () => ({
        validateForm
    }));

    return (
        <div className='personal-form-container'>
            <div className='input-container'>
                <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    placeholder="First name"
                    value={personalInfo.first_name}
                    onChange={handleInputChange}
                    aria-label="Enter your first name"
                />
                {validationErrors.first_name && (
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                )}
            </div>

            <div className='input-container'>
                <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    placeholder="Last name"
                    value={personalInfo.last_name}
                    onChange={handleInputChange}
                    aria-label="Enter your last name"
                />
                {validationErrors.last_name && (
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                )}
            </div>

            <div className='input-container'>
                <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email"
                    value={personalInfo.email}
                    onChange={handleInputChange}
                    aria-label="Enter your email"
                />
                {validationErrors.email && (
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                )}
            </div>

            <div className='input-container'>
                {/* #272. `onPaste` is deliberately NOT blocked: it would punish everyone,
                    password-manager users most of all, and someone determined to paste both
                    fields defeats the check anyway. This catches the single mistyped character,
                    which is what the four bad addresses in the database actually are. */}
                <input
                    type="email"
                    id="confirm_email"
                    name="confirm_email"
                    placeholder="Confirm email"
                    value={personalInfo.confirm_email}
                    onChange={handleInputChange}
                    aria-label="Type your email address again to confirm it"
                />
                {validationErrors.confirm_email && (
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                )}
            </div>
            {/* Spelt out rather than left to the error icon: an icon beside a field the
                customer believes they filled in correctly says nothing about what is wrong. */}
            {validationErrors.confirm_email && personalInfo.confirm_email && (
                <div className='field-message'>
                    The two email addresses do not match. Your booking confirmation and door
                    code are sent to this address, so please ensure it is correct.
                </div>
            )}

            <div className='input-container'>
                <label className='checkbox-container'>
                    <input className='checkbox'
                        type="checkbox"
                        id="termsAccepted"
                        name="termsAccepted"
                        checked={personalInfo.termsAccepted}
                        onChange={handleInputChange}
                    />
                    <span>I accept your <a href="/terms" target='_blank' rel='noopener noreferrer' >terms and conditions</a></span>

                </label>
                {validationErrors.termsAccepted && (
                    <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                )}
            </div>
        </div>
    );
});

export default PersonalInfoForm;
